import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import api from "../../api/axios";
import styles from "./Admin.module.css";

const EMPTY_FORM = { name: "", description: "", price: "", category: "", brand: "", stock: "", tags: "" };

const ProductThumb = ({ images, name }) => {
  const url = images?.[0]?.url;
  const [err, setErr] = useState(false);
  if (!url || err) return <div className={styles.thumbPlaceholder}>{name?.charAt(0)?.toUpperCase() || "◈"}</div>;
  return <img src={url} alt={name} onError={() => setErr(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [editImages, setEditImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileRef = useRef();

  const fetchProducts = () => {
    api.get("/products?limit=50").then(r => setProducts(r.data.products)).finally(() => setLoading(false));
  };
  useEffect(fetchProducts, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (imageFile) fd.append("images", imageFile);
      const config = { headers: { "Content-Type": "multipart/form-data" } };

      if (editId) {
        await api.put(`/products/${editId}`, fd, config);
        toast.success("Product updated!");
      } else {
        await api.post("/products", fd, config);
        toast.success("Product created!");
      }
      setForm(EMPTY_FORM); setEditId(null); setShowForm(false);
      setImageFile(null); setImagePreview(null); setEditImages([]);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save product");
    } finally { setSubmitting(false); }
  };

  const handleEdit = (p) => {
    setForm({
      name: p.name, description: p.description, price: p.price,
      category: p.category, brand: p.brand || "", stock: p.stock,
      tags: p.tags?.join(", ") || "",
    });
    setEditId(p._id);
    setEditImages(p.images || []);
    setImageFile(null);
    setImagePreview(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch { toast.error("Delete failed"); }
  };

  const cancelForm = () => {
    setForm(EMPTY_FORM); setEditId(null); setShowForm(false);
    setImageFile(null); setImagePreview(null); setEditImages([]);
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Products</h1>
        <button className="btn btn-primary" onClick={() => { if (showForm) cancelForm(); else setShowForm(true); }}>
          {showForm ? "Cancel" : "+ Add Product"}
        </button>
      </div>

      {showForm && (
        <div className={`${styles.section} fade-up`} style={{ marginBottom: "24px" }}>
          <h3 className={styles.sectionTitle} style={{ marginBottom: "20px" }}>
            {editId ? "Edit product" : "New product"}
          </h3>

          <form onSubmit={handleSubmit}>
            <div className={styles.formGrid}>
              <div className="form-group">
                <label className="label">Product name *</label>
                <input className="input" value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Samsung Galaxy M34" />
              </div>
              <div className="form-group">
                <label className="label">Category *</label>
                <input className="input" value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })} required placeholder="phones" />
              </div>
              <div className="form-group">
                <label className="label">Brand</label>
                <input className="input" value={form.brand}
                  onChange={e => setForm({ ...form, brand: e.target.value })} placeholder="Samsung" />
              </div>
              <div className="form-group">
                <label className="label">Price (₹) *</label>
                <input className="input" type="number" value={form.price}
                  onChange={e => setForm({ ...form, price: e.target.value })} required placeholder="18999" />
              </div>
              <div className="form-group">
                <label className="label">Stock *</label>
                <input className="input" type="number" value={form.stock}
                  onChange={e => setForm({ ...form, stock: e.target.value })} required placeholder="10" />
              </div>
              <div className="form-group">
                <label className="label">Tags (comma separated)</label>
                <input className="input" value={form.tags}
                  onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="5g, budget, camera" />
              </div>
            </div>

            <div className="form-group">
              <label className="label">Description *</label>
              <textarea className="input" rows={3} value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                required placeholder="Product description..." style={{ resize: "vertical" }} />
            </div>

            <div className="form-group">
              <label className="label">Product image</label>
              <div className={styles.imageUploadRow}>
                {/* Show existing image if editing */}
                {editImages[0]?.url && !imagePreview && (
                  <div className={styles.imagePreview}>
                    <img src={editImages[0].url} alt="current"
                      onError={e => e.target.parentNode.style.display = "none"} />
                    <div className={styles.imageLabel}>Current</div>
                  </div>
                )}
                {/* Show new image preview */}
                {imagePreview && (
                  <div className={styles.imagePreview}>
                    <img src={imagePreview} alt="new" />
                    <div className={styles.imageLabel}>New</div>
                  </div>
                )}
                <div>
                  <input ref={fileRef} type="file" accept="image/*"
                    style={{ display: "none" }} onChange={handleImageChange} />
                  <button type="button" className="btn btn-ghost" onClick={() => fileRef.current.click()}>
                    {imageFile ? `✓ ${imageFile.name}` : editImages[0]?.url ? "Change image" : "Upload image"}
                  </button>
                  {imageFile && (
                    <button type="button" className="btn btn-ghost"
                      style={{ marginLeft: "8px" }}
                      onClick={() => { setImageFile(null); setImagePreview(null); fileRef.current.value = ""; }}>
                      ✕ Remove
                    </button>
                  )}
                  <p style={{ fontSize: "12px", color: "var(--text3)", marginTop: "6px" }}>
                    JPG, PNG or WEBP. Max 5MB.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? <span className="spinner" /> : (editId ? "Update product" : "Create product")}
              </button>
              <button type="button" className="btn btn-ghost" onClick={cancelForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className={styles.section}>
          {products.length === 0 ? (
            <div className="empty-state"><h3>No products yet</h3><p>Click + Add Product to create one</p></div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p._id}>
                    <td>
                      <div className={styles.productCell}>
                        <div className={styles.productThumb}>
                          <ProductThumb images={p.images} name={p.name} />
                        </div>
                        <div>
                          <div className={styles.productCellName}>{p.name}</div>
                          {p.brand && <div className={styles.productCellBrand}>{p.brand}</div>}
                        </div>
                      </div>
                    </td>
                    <td><span className="badge badge-gray">{p.category}</span></td>
                    <td className={styles.priceCell}>₹{p.price?.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${p.stock === 0 ? "badge-red" : p.stock <= 5 ? "badge-amber" : "badge-green"}`}>
                        {p.stock}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button className="btn btn-ghost" style={{ padding: "6px 12px", fontSize: "13px" }}
                          onClick={() => handleEdit(p)}>Edit</button>
                        <button className="btn btn-danger" style={{ padding: "6px 12px", fontSize: "13px" }}
                          onClick={() => handleDelete(p._id, p.name)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
